import { useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableHead,
  TableRow,
  TextField,
  Link,
  Typography,
  Paper,
  CircularProgress,
} from "@mui/material";
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";

import useData from "../../hooks/useData";
import usePage from "../../hooks/usePage";

import Page from "./Page";
import Pagination from "../elements/Pagination";

const REFRESH_MS = 30000;
const STATUS_OPTIONS = [
  "waiting",
  "processing",
  "restart",
  "completed",
  "error",
];

const formatNumber = (value, decimals = 0) => {
  const numberValue = Number(value || 0);
  if (!Number.isFinite(numberValue)) return "0";
  return numberValue.toLocaleString(undefined, {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  });
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const formatElapsedMinutes = (value) => {
  const totalMinutes = Number(value || 0);
  if (!Number.isFinite(totalMinutes) || totalMinutes < 0) return "-";

  const roundedMinutes = Math.floor(totalMinutes);
  const days = Math.floor(roundedMinutes / 1440);
  const hours = Math.floor((roundedMinutes % 1440) / 60);
  const minutes = roundedMinutes % 60;

  if (days > 0) {
    return `${days}d ${hours}h`;
  }

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m`;
};

const formatProgressDisplay = (row) => {
  const progress = Number(row?.progress || 0);
  const totalItems = Number(row?.totalItems || 0);
  const itemsProcessed = Number(row?.itemsProcessed || 0);
  const status = String(row?.status || "").toLowerCase();

  if (totalItems > 0) {
    return `${formatNumber(itemsProcessed)} / ${formatNumber(totalItems)} (${formatNumber(progress, 0)}%)`;
  }

  if (status === "completed") {
    return "100%";
  }

  if (status === "error") {
    return "-";
  }

  return `${formatNumber(progress, 0)}%`;
};

const WorkQueueMonitor = () => {
  const { executeAction } = useData();
  const { showMessage } = usePage();

  const [summary, setSummary] = useState(null);
  const [workTypeStats, setWorkTypeStats] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    statuses: STATUS_OPTIONS,
    workTypes: [],
    createdBy: [],
  });

  const [rows, setRows] = useState([]);
  const [recordCount, setRecordCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(25);

  const [statusFilter, setStatusFilter] = useState("processing");
  const [workTypeFilter, setWorkTypeFilter] = useState("");
  const [createdByFilter, setCreatedByFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");

  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [workTypeStatsExpanded, setWorkTypeStatsExpanded] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedDetailsRow, setSelectedDetailsRow] = useState(null);

  const detailsPreviewText = useMemo(() => {
    const details = selectedDetailsRow?.details;
    if (!details || typeof details !== "object") {
      return "No details available.";
    }

    try {
      return JSON.stringify(details, null, 2);
    } catch (_err) {
      return "Unable to format details.";
    }
  }, [selectedDetailsRow]);

  const filters = useMemo(() => {
    const statusList = statusFilter ? [statusFilter] : [];
    const workTypeList = workTypeFilter ? [workTypeFilter] : [];

    return {
      statuses: statusList,
      workTypes: workTypeList,
      createdBy: createdByFilter || undefined,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      search: search || undefined,
    };
  }, [statusFilter, workTypeFilter, createdByFilter, fromDate, toDate, search]);

  const summaryFilters = useMemo(
    () => ({
      statuses: [],
      workTypes: filters.workTypes,
      createdBy: filters.createdBy,
      fromDate: filters.fromDate,
      toDate: filters.toDate,
    }),
    [
      filters.workTypes.join(","),
      filters.createdBy,
      filters.fromDate,
      filters.toDate,
    ],
  );

  useEffect(() => {
    let cancelled = false;

    const loadSummary = async () => {
      setLoadingSummary(true);
      try {
        const results = await executeAction({
          entityName: "WorkQueue",
          action: "getMonitorSummary",
          data: { filters: summaryFilters },
        });

        if (cancelled) return;

        if (!results?.isSuccessful) {
          showMessage(
            results?.message || "Unable to load queue summary",
            "error",
          );
          return;
        }

        setSummary(results.data?.summary || null);
        setWorkTypeStats(results.data?.workTypeStats || []);
        setFilterOptions(
          results.data?.filterOptions || {
            statuses: STATUS_OPTIONS,
            workTypes: [],
            createdBy: [],
          },
        );
        setLastRefreshedAt(new Date());
      } catch (error) {
        if (!cancelled) {
          showMessage(
            `Unable to load queue summary: ${error.message}`,
            "error",
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingSummary(false);
        }
      }
    };

    loadSummary();

    return () => {
      cancelled = true;
    };
  }, [
    summaryFilters.fromDate,
    summaryFilters.toDate,
    summaryFilters.createdBy,
    summaryFilters.workTypes.join(","),
  ]);

  useEffect(() => {
    let cancelled = false;

    const loadRows = async () => {
      setLoadingRows(true);
      try {
        const results = await executeAction({
          entityName: "WorkQueue",
          action: "getMonitorRows",
          data: {
            filters,
            pageNumber,
            recordsPerPage,
          },
        });

        if (cancelled) return;

        if (!results?.isSuccessful) {
          showMessage(results?.message || "Unable to load queue rows", "error");
          return;
        }

        const rowsData = results.data?.records || [];
        setRows(rowsData);
        setRecordCount(results.data?.recordCount || 0);
        setLastRefreshedAt(new Date());
      } catch (error) {
        if (!cancelled) {
          showMessage(`Unable to load queue rows: ${error.message}`, "error");
        }
      } finally {
        if (!cancelled) {
          setLoadingRows(false);
        }
      }
    };

    loadRows();

    return () => {
      cancelled = true;
    };
  }, [
    filters.fromDate,
    filters.toDate,
    filters.createdBy,
    filters.search,
    filters.statuses.join(","),
    filters.workTypes.join(","),
    pageNumber,
    recordsPerPage,
  ]);

  useEffect(() => {
    const timer = setInterval(async () => {
      const [summaryResults, rowResults] = await Promise.all([
        executeAction({
          entityName: "WorkQueue",
          action: "getMonitorSummary",
          data: { filters: summaryFilters },
        }),
        executeAction({
          entityName: "WorkQueue",
          action: "getMonitorRows",
          data: {
            filters,
            pageNumber,
            recordsPerPage,
          },
        }),
      ]);

      if (summaryResults?.isSuccessful) {
        setSummary(summaryResults.data?.summary || null);
        setWorkTypeStats(summaryResults.data?.workTypeStats || []);
        setFilterOptions(
          summaryResults.data?.filterOptions || {
            statuses: STATUS_OPTIONS,
            workTypes: [],
            createdBy: [],
          },
        );
      }

      if (rowResults?.isSuccessful) {
        setRows(rowResults.data?.records || []);
        setRecordCount(rowResults.data?.recordCount || 0);
      }

      setLastRefreshedAt(new Date());
    }, REFRESH_MS);

    return () => clearInterval(timer);
  }, [
    summaryFilters.fromDate,
    summaryFilters.toDate,
    summaryFilters.createdBy,
    summaryFilters.workTypes.join(","),
    filters.search,
    filters.statuses.join(","),
    pageNumber,
    recordsPerPage,
  ]);

  return (
    <Page pageName="Work Queue Monitor">
      <Stack spacing={2}>
        <Card sx={{ order: 2 }}>
          <CardContent>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h6">Queue Items</Typography>
              <Button
                size="small"
                onClick={() => setFiltersExpanded((value) => !value)}
                endIcon={
                  filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
                }
              >
                {filtersExpanded ? "Hide Filters" : "Show Filters"}
              </Button>
            </Stack>

            <Collapse in={filtersExpanded}>
              <Box
                sx={{
                  mt: 2,
                  mb: 2,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  flexWrap: "wrap",
                  columnGap: 1.5,
                  rowGap: 1.5,
                }}
              >
                <FormControl
                  size="small"
                  sx={{ width: 180 }}
                >
                  <InputLabel id="queue-monitor-status-label">
                    Status
                  </InputLabel>
                  <Select
                    labelId="queue-monitor-status-label"
                    value={statusFilter}
                    label="Status"
                    onChange={(event) => {
                      setStatusFilter(event.target.value);
                      setPageNumber(0);
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {(filterOptions.statuses || STATUS_OPTIONS).map(
                      (status) => (
                        <MenuItem
                          key={status}
                          value={status}
                        >
                          {status}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{ width: 240 }}
                >
                  <InputLabel id="queue-monitor-worktype-label">
                    Work Type
                  </InputLabel>
                  <Select
                    labelId="queue-monitor-worktype-label"
                    value={workTypeFilter}
                    label="Work Type"
                    onChange={(event) => {
                      setWorkTypeFilter(event.target.value);
                      setPageNumber(0);
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {(filterOptions.workTypes || []).map((workType) => (
                      <MenuItem
                        key={workType}
                        value={workType}
                      >
                        {workType}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{ width: 240 }}
                >
                  <InputLabel id="queue-monitor-createdby-label">
                    Requested By
                  </InputLabel>
                  <Select
                    labelId="queue-monitor-createdby-label"
                    value={createdByFilter}
                    label="Requested By"
                    onChange={(event) => {
                      setCreatedByFilter(event.target.value);
                      setPageNumber(0);
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {(filterOptions.createdBy || []).map((item) => (
                      <MenuItem
                        key={item.value}
                        value={item.value}
                      >
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  label="From"
                  type="date"
                  sx={{ width: 170 }}
                  value={fromDate}
                  onChange={(event) => {
                    setFromDate(event.target.value);
                    setPageNumber(0);
                  }}
                  slotProps={{ inputLabel: { shrink: true } }}
                />

                <TextField
                  size="small"
                  label="To"
                  type="date"
                  sx={{ width: 170 }}
                  value={toDate}
                  onChange={(event) => {
                    setToDate(event.target.value);
                    setPageNumber(0);
                  }}
                  slotProps={{ inputLabel: { shrink: true } }}
                />

                <TextField
                  size="small"
                  label="Search"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPageNumber(0);
                  }}
                  placeholder="Work type, status, user, email"
                  sx={{ width: 320 }}
                />
              </Box>
            </Collapse>

            <TableContainer
              component={Paper}
              variant="outlined"
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 180, whiteSpace: "nowrap" }}>
                      Created
                    </TableCell>
                    <TableCell>Work Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Requested By</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell align="right">Progress</TableCell>
                    <TableCell align="right">Age</TableCell>
                    <TableCell align="right">Duration (min)</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingRows ? (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <CircularProgress size={18} />
                          <Typography variant="body2">
                            Loading queue rows...
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          No rows found for current filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell sx={{ minWidth: 180, whiteSpace: "nowrap" }}>
                          {formatDateTime(row.createdAt)}
                        </TableCell>
                        <TableCell>{row.workType || "-"}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.status || "-"}
                            color={
                              row.status === "error"
                                ? "error"
                                : row.status === "completed"
                                  ? "success"
                                  : row.status === "processing"
                                    ? "info"
                                    : "default"
                            }
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{row.createdByName || "-"}</TableCell>
                        <TableCell>
                          {row.details &&
                          typeof row.details === "object" &&
                          Object.keys(row.details).length > 0 ? (
                            <Link
                              component="button"
                              underline="hover"
                              onClick={() => {
                                setSelectedDetailsRow(row);
                                setDetailsModalOpen(true);
                              }}
                            >
                              View
                            </Link>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {formatProgressDisplay(row)}
                        </TableCell>
                        <TableCell align="right">
                          {["waiting", "processing", "restart"].includes(
                            row.status,
                          )
                            ? formatElapsedMinutes(row.ageMinutes)
                            : "-"}
                        </TableCell>
                        <TableCell align="right">
                          {row.durationMinutes == null
                            ? "-"
                            : formatNumber(row.durationMinutes, 1)}
                        </TableCell>
                        <TableCell>{row.errorDetails || "-"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 1 }}>
              <Pagination
                pageNumber={pageNumber}
                recordsPerPage={recordsPerPage}
                recordCount={recordCount}
                onPageChange={(newPage) => setPageNumber(newPage)}
                onRecordsPerPageChange={(newRowsPerPage) => {
                  setRecordsPerPage(newRowsPerPage);
                  setPageNumber(0);
                }}
              />
            </Box>
          </CardContent>
        </Card>

        <Stack
          sx={{ order: 1 }}
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
        >
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography
                variant="subtitle2"
                color="text.secondary"
              >
                Queue Status
              </Typography>
              {loadingSummary ? (
                <Box sx={{ py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mt: 1 }}
                >
                  <Chip
                    label={`Total ${formatNumber(summary?.total)}`}
                    color="default"
                  />
                  <Chip
                    label={`Waiting ${formatNumber(summary?.waiting)}`}
                    color="warning"
                  />
                  <Chip
                    label={`Processing ${formatNumber(summary?.processing)}`}
                    color="info"
                  />
                  <Chip
                    label={`Completed Today ${formatNumber(summary?.completedToday)}`}
                    color="success"
                  />
                  <Chip
                    label={`Errors Today ${formatNumber(summary?.errorToday)}`}
                    color="error"
                  />
                </Stack>
              )}
            </CardContent>
          </Card>

          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Typography
                variant="subtitle2"
                color="text.secondary"
              >
                Timings
              </Typography>
              {loadingSummary ? (
                <Box sx={{ py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : (
                <Stack
                  spacing={0.5}
                  sx={{ mt: 1 }}
                >
                  <Typography variant="body2">
                    Average active age:{" "}
                    {formatNumber(summary?.avgActiveAgeMinutes, 1)} min
                  </Typography>
                  <Typography variant="body2">
                    Oldest waiting:{" "}
                    {formatNumber(summary?.oldestWaitingMinutes, 1)} min
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Last refreshed:{" "}
                    {lastRefreshedAt
                      ? lastRefreshedAt.toLocaleTimeString()
                      : "-"}
                  </Typography>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Stack>

        <Card sx={{ order: 3 }}>
          <CardContent>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography variant="h6">Work Type Stats</Typography>
              <Button
                size="small"
                onClick={() => setWorkTypeStatsExpanded((value) => !value)}
                endIcon={
                  workTypeStatsExpanded ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )
                }
              >
                {workTypeStatsExpanded ? "Collapse" : "Expand"}
              </Button>
            </Stack>

            <Collapse in={workTypeStatsExpanded}>
              <TableContainer
                component={Paper}
                variant="outlined"
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Work Type</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Waiting</TableCell>
                      <TableCell align="right">Processing</TableCell>
                      <TableCell align="right">Completed</TableCell>
                      <TableCell align="right">Error</TableCell>
                      <TableCell align="right">Avg Duration (min)</TableCell>
                      <TableCell align="right">Max Duration (min)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {workTypeStats.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                          >
                            No rows found for current filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      workTypeStats.map((row) => (
                        <TableRow key={row.workType}>
                          <TableCell>{row.workType}</TableCell>
                          <TableCell align="right">
                            {formatNumber(row.total)}
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(row.waiting)}
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(row.processing)}
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(row.completed)}
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(row.error)}
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(row.avgDurationMinutes, 1)}
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(row.maxDurationMinutes, 1)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </CardContent>
        </Card>

        <Dialog
          open={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Queue Item Details
            {selectedDetailsRow?.workType
              ? ` - ${selectedDetailsRow.workType}`
              : ""}
          </DialogTitle>
          <DialogContent dividers>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                borderRadius: 1,
                backgroundColor: "#f8f9fb",
                border: "1px solid #e0e0e0",
                fontSize: 12,
                overflowX: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {detailsPreviewText}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsModalOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Page>
  );
};

export default WorkQueueMonitor;
